import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { handoffAiJobToBackground } from '@/components/ai-job/aiJobHandoff';
import { useAiJobMutation } from '@/hooks/useAiJobMutation';

export interface AiGenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  /** Absolute API path that returns 202 with `{ data: AiJobState }`. */
  endpoint: string;
  /** Query keys to invalidate on success. */
  invalidateKeys: readonly (readonly unknown[])[];
  /** Toast text shown when the background job completes. */
  successMessage: string;
  /** Fallback error message if the dispatch fails. */
  errorFallback?: string;
  /** Submit button label. Defaults to the dialog `title`. */
  submitLabel?: string;
  /** Produces the POST body. Return null to abort. */
  buildPayload: () => Record<string, unknown> | null;
  /** Form controls rendered between header and footer. */
  children?: ReactNode;
}

/**
 * Drop-in replacement for the shared `GenerateDialog` that routes work
 * through the on-demand AI-job pipeline:
 *
 * - Submit posts to `endpoint` (which returns 202 with a jobId).
 * - The dialog shows a loading state while the background job is running.
 * - Auto-closes + invalidates queries + toasts when the job finishes.
 * - Adds a **"Continue in background"** button that dismisses the dialog
 *   and registers the job with the global notification provider; a global
 *   toast fires when the job eventually completes.
 *
 * The button signature matches `GenerateDialog`, so call sites only need to
 * change the import.
 */
export function AiGenerateDialog({
  open,
  onOpenChange,
  title,
  description,
  endpoint,
  invalidateKeys,
  successMessage,
  errorFallback = 'Generation failed to start',
  submitLabel,
  buildPayload,
  children,
}: AiGenerateDialogProps) {
  const queryClient = useQueryClient();
  const job = useAiJobMutation({ endpoint, errorLabel: errorFallback });
  const [hasHandedOff, setHasHandedOff] = useState(false);

  // Track which terminal status we've already reacted to so we don't
  // re-fire the toast on rerenders while the dialog closes.
  const handledStatusRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      // Reset the per-dialog state when reopening.
      job.reset();
      setHasHandedOff(false);
      handledStatusRef.current = null;
    }
    // We intentionally don't include `job` in deps — it's a fresh object
    // every render and would loop us. We only care about `open` toggling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!job.state || hasHandedOff) return;
    if (handledStatusRef.current === job.state.status) return;

    if (job.state.status === 'completed') {
      handledStatusRef.current = 'completed';
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [...key] });
      });
      toast.success(successMessage);
      onOpenChange(false);
    } else if (job.state.status === 'failed') {
      handledStatusRef.current = 'failed';
      toast.error(job.state.error ?? errorFallback);
      // Keep the dialog open so the user can retry.
    }
  }, [
    job.state,
    hasHandedOff,
    invalidateKeys,
    queryClient,
    onOpenChange,
    successMessage,
    errorFallback,
  ]);

  // "Working" spans from dispatch until a terminal status — not just while the
  // socket reports `running` — so the dialog doesn't flip back to an idle
  // submit state in the gap before the first status arrives. A stall breaks us
  // out so the user can retry instead of waiting on a job that never reports.
  const isWorking =
    job.isStarting || (job.jobId != null && !job.isFinished && !job.isStalled);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isWorking) return;
    const payload = buildPayload();
    if (payload === null) return;
    await job.start(payload);
  };

  const handleHandoff = () => {
    if (!job.jobId) return;
    handoffAiJobToBackground(job.jobId);
    setHasHandedOff(true);
    toast.success("Generation moved to background — we'll notify you when it finishes.");
    onOpenChange(false);
  };

  const handleCancelClose = () => {
    if (isWorking) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next: boolean) => {
        // Block backdrop / escape close while a job is in flight unless the
        // user has explicitly handed off — otherwise they could lose track
        // of work that's still running on the server.
        if (!next && isWorking) return;
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          {children && <div className="space-y-4 py-4">{children}</div>}

          {isWorking && (
            <p className="text-sm text-muted-foreground">
              Generating… this usually takes 30–60 seconds. You can keep this
              open or click <strong>Continue in background</strong> to be
              notified when it finishes.
            </p>
          )}

          {job.isStalled && !isWorking && (
            <p className="text-sm text-destructive">
              This is taking longer than expected — the job may not have started.
              Try again, or check back shortly.
            </p>
          )}

          <DialogFooter className="gap-3">
            {isWorking ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleHandoff}
                  disabled={!job.jobId}
                >
                  Continue in background
                </Button>
                <Button type="button" loading disabled>
                  {submitLabel ?? title}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelClose}
                >
                  Cancel
                </Button>
                <Button type="submit">{submitLabel ?? title}</Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
